import { useState, Fragment } from "react";

import { Edit, FileExport, Plus } from "tabler-icons-react";
import { Button, Modal, Table, Alert, Grid, Text, MediaQuery, Menu, ActionIcon, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import TableRowActions from "./shared/table-row-actions";
import { useForm } from "@mantine/form";
import { updateDoc, doc, deleteDoc, collection, addDoc } from "firebase/firestore";

import { Zone, Section } from "@captiva/shared-types";
import { formatValue } from "../../components/listings/hooks";
import { DataEntry } from "../../components/shared/data-entry";
import { db } from "../../firebase";
import { SectionAssetConfig } from "../../components/listings/configurations/assets/section-asset-confing";
import { ZoneAssetConfig } from "../../components/listings/configurations/assets/zone-asset-config";
import { FlexOptionsChoiceModal } from "./shared/flex-options-choice-modal";
import { useCreateFlexOptionsSet } from "../../hooks/useCreateFlexOptionsSet";

export interface AuthUser {
  uid: string;
  email?: string;
  claims?: {
    companyId?: string;
  };
}

interface AssetConfigField {
  type: string;
  required?: boolean;
  editable?: boolean;
  label: string;
  rank?: number;
}

interface ZoneEditorProps<T extends Record<string, any>> {
  asset: T;
  assetConfig: Record<string, AssetConfigField>;
  saveAssetCallback: (updatedAsset: T) => Promise<void> | void;
  deleteAssetCallback: (deleteCandidate: T) => Promise<void> | void;
}

export function ZoneEditor<T extends Record<string, any>>({
  asset,
  assetConfig,
  saveAssetCallback,
  deleteAssetCallback,
}: ZoneEditorProps<T>) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [initialValues] = useState<T>({ ...asset });
  const [deleteCandidate, setDeleteCandidate] = useState<T | undefined>(undefined);
  
  function buildValidationRules(config: Record<string, AssetConfigField>) {
    const validationRulesConfig: Record<string, (value: any) => string | null> = {};
    Object.keys(config).forEach((key) => {
      // check if required (ignore booleans)
      if (config[key].required && config[key].type !== "BOOLEAN") {
        validationRulesConfig[key] = (value) => (!value ? "Required" : null);
      }
      // right now all numbers are required check if positive number
      // todo future: handle non-required number case
      if (config[key].type === "NUMBER") {
        validationRulesConfig[key] = (value) =>
          isNaN(Number(value)) || Number(value) < 0
            ? "Must be a positive number"
            : null;
      }
    });
    return validationRulesConfig;
  }

  const form = useForm({
    initialValues,
    validate: buildValidationRules(assetConfig) as any,
  });

  async function saveAsset(updatedAsset: T) {
    saveAssetCallback(updatedAsset);
  }

  async function deleteAsset(candidate: T) {
    deleteAssetCallback(candidate);
  }

  return (
    <>
      <form onSubmit={form.onSubmit((values) => saveAsset(values as T))}>
        {Object.keys(initialValues)
          .filter((k) => assetConfig[k])
          .sort((a, b) => (assetConfig[a]?.rank ?? 0) - (assetConfig[b]?.rank ?? 0))
          .map((key) => (
            <div
              key={key}
              style={{
                marginTop: "1em",
              }}
            >
              <DataEntry
                key={key}
                label={assetConfig[key]?.label}
                edit={assetConfig[key]?.editable}
                inputProps={form.getInputProps(key)}
                dataType={assetConfig[key]?.type}
                form={form}
                fieldKey={key}
                value={formatValue(assetConfig[key]?.type, asset[key])}
              />
            </div>
          ))}
        <Button size="md" color="cap-navy" fullWidth type="submit" mt="md" style={{ minHeight: 44 }}>
          Save
        </Button>
      </form>
      {asset.id && (
        <>
          {deleteCandidate ? (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                borderRadius: 8,
                backgroundColor: "#fff5f5",
                border: "1px solid #ffc9c9",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                gap: 12,
              }}
            >
              <Text size="md" weight={500} color="red" style={{ flex: 1, textAlign: isMobile ? "center" : "left" }}>
                Are you sure?
              </Text>
              <div
                style={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 12,
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <Button
                  color="gray"
                  variant="default"
                  size="md"
                  fullWidth={isMobile}
                  style={{ minHeight: 44 }}
                  onClick={() => setDeleteCandidate(undefined)}
                >
                  Cancel
                </Button>
                <Button
                  color="red"
                  size="md"
                  fullWidth={isMobile}
                  style={{ minHeight: 44 }}
                  onClick={() => deleteAsset(asset)}
                >
                  Yes - Delete
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="md"
              color="red"
              fullWidth
              mt="md"
              style={{ minHeight: 44 }}
              onClick={() => setDeleteCandidate(asset)}
            >
              Delete
            </Button>
          )}
        </>
      )}
    </>
  );
}

interface ZonesTableProps {
  elements: Zone[];
  auth: AuthUser | null;
  reloadCallback: () => void;
}

export function ZonesTable({ elements, auth, reloadCallback }: ZonesTableProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const newSection: Partial<Section> = {
    sectionName: "",
    isActive: true,
    areaSizeHectares: 0,
  };

  const newZone: Partial<Zone> = {
    zoneName: "",
    sections: [],
    isActive: true,
  };

  const [editedZone, setEditedZone] = useState<Zone | Partial<Zone> | undefined>(undefined);
  const [editedSection, setEditedSection] = useState<Section | Partial<Section> | undefined>(undefined);
  const [editedSectionZone, setEditedSectionZone] = useState<Zone | undefined>(undefined);
  const [editedSectionIndex, setEditedSectionIndex] = useState<number | undefined>(undefined);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [messageColor, setMessageColor] = useState<string>("green");
  const closeZoneEdit = () => setEditedZone(undefined);
  const closeSectionEdit = () => { setEditedSection(undefined); setEditedSectionIndex(undefined); };
  const targetCollection = "areaForFleetUse";
  const [createFlexOptionsSet, { loading, notification, clearNotification, existingDocs, mergeOptions, createNewSet }] = useCreateFlexOptionsSet();

  // Handler to export Sections with Zone metadata
  const handleExportSectionsToFlexOptions = () => {
    const companyId = auth?.claims?.companyId;
    if (!companyId) {
      setMessageColor("red");
      createTimedMessage("No company ID found in auth claims.");
      return;
    }
    // Gather all sections with their zone metadata
    const allSections = elements.flatMap(zone =>
      (zone.sections || []).map((section: Section) => ({
        ...section,
        zoneName: zone.zoneName,
        zoneId: zone.id
      }))
    );
    createFlexOptionsSet({
      assetType: "Sections",
      assetList: allSections,
      metadataPrefix: "parent",
      fieldMappings: {
        label: "sectionName",
        value: "id",
        metadata: {
          zoneName: "zoneName",
          zoneId: "zoneId",
          sectionName: "sectionName",
        }
      },
      companyId
    });
  };

  const createTimedMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(""), 2000);
  };

  const finishEditWithMessage = (msg: string) => {
    setMessageColor("green");
    createTimedMessage(msg);
    closeZoneEdit();
    closeSectionEdit();
    reloadCallback();
  };

  const finishEditWithErrorMessage = (msg: string) => {
    setMessageColor("red");
    createTimedMessage(msg);
    closeZoneEdit();
    closeSectionEdit();
    reloadCallback();
  };

  const updateZone = async (updatedZone: Zone) => {
    if (!targetCollection || !auth || !updatedZone || !updatedZone.id) {
      finishEditWithErrorMessage("Save unsuccessful.");
      return;
    }
    const docRef = doc(db, targetCollection, updatedZone.id);
    await updateDoc(docRef, updatedZone as any);
    finishEditWithMessage("Successfully saved!");
  };

  const createZone = async (candidateZone: Partial<Zone>) => {
    const companyId = auth?.claims?.companyId;
    if (!targetCollection || !auth || !candidateZone || !companyId) {
      finishEditWithErrorMessage("Save unsuccessful.");
      return;
    }
    await addDoc(collection(db, targetCollection), {
      ...candidateZone,
      companyId,
    });
    finishEditWithMessage("Successfully created!");
  };

  // can only update zone name and active
  const onSaveZone = async (updatedZone: Zone | Partial<Zone>) => {
    const mergedZone = { ...editedZone, ...updatedZone } as Zone | Partial<Zone>;
    if (mergedZone.id) {
      await updateZone(mergedZone as Zone);
    } else {
      await createZone(mergedZone);
    }
  };

  const handleDeleteZone = async (deletionCandidateZone: Zone | Partial<Zone>) => {
    try {
      if (!deletionCandidateZone.id) return;
      const docRef = doc(db, targetCollection, deletionCandidateZone.id);
      await deleteDoc(docRef);
      finishEditWithMessage("Delete successful.");
    } catch (error: any) {
      setMessageColor("red");
      finishEditWithErrorMessage("Delete unsuccessful: " + error.toString());
    }
  };

  const handleSaveSection = async (sectionCandidate: Section | Partial<Section>) => {
    if (!editedSectionZone) return;
    const updatedZone = { ...editedSectionZone };
    if (editedSectionIndex !== undefined && editedSectionIndex >= 0) {
      const updatedSection = { ...editedSection, ...sectionCandidate } as Section;
      const sections = [...(updatedZone.sections || [])];
      sections[editedSectionIndex] = updatedSection;
      updatedZone.sections = sections;
      await onSaveZone(updatedZone);
      finishEditWithMessage("Successfully updated!");
    } else {
      const sections = [...(updatedZone.sections || [])];
      const newSec = { ...sectionCandidate, id: `${updatedZone.zoneName} - ${sectionCandidate.sectionName}` } as Section;
      sections.push(newSec);
      updatedZone.sections = sections;
      await onSaveZone(updatedZone);
      finishEditWithMessage("Successfully created!");
    }
  };

  const handleDeleteSection = async (deletionCandidateSection: Section | Partial<Section>) => {
    if (!editedSectionZone) return;
    const indexToRemove = editedSectionZone.sections?.findIndex((s: Section) => s.id === deletionCandidateSection.id) ?? -1;
    if (indexToRemove >= 0) {
      const updatedZone = { ...editedSectionZone };
      const sections = [...(updatedZone.sections || [])];
      sections.splice(indexToRemove, 1);
      updatedZone.sections = sections;
      await onSaveZone(updatedZone);
      finishEditWithMessage("Successfully updated!");
    }
  };

  const SectionList = ({ element }: { element: Zone }) => (
    <td>
      {element.sections?.length ? (
        <>
          <MediaQuery largerThan="sm" styles={{ display: "none" }}>
            <Table>
              <thead>
                <tr>
                  <th>Section name</th>
                </tr>
              </thead>
              <tbody>
                {element.sections?.map((section: Section, index: number) => (
                  <tr key={section.id}>
                    <td>{section.sectionName}</td>

                    <td>
                      <Tooltip label="Edit Section" withArrow position="top">
                        <ActionIcon
                          size="lg"
                          color="cap-blue"
                          variant="outline"
                          onClick={() => {
                            setEditedSection(section);
                            setEditedSectionZone(element);
                            setEditedSectionIndex(index);
                          }}
                        >
                          <Edit size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </MediaQuery>
          <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
            <Table>
              <thead>
                <tr>
                  <th>Section name</th>
                  <th>Section size</th>
                </tr>
              </thead>
              <tbody>
                {element.sections?.map((section: Section, index: number) => (
                  <tr key={section.id}>
                    <td>{section.sectionName}</td>
                    <td>{section.areaSizeHectares}</td>
                    <td>
                      <Tooltip label="Edit Section" withArrow position="top">
                        <ActionIcon
                          size="lg"
                          color="cap-blue"
                          variant="outline"
                          onClick={() => {
                            setEditedSection(section);
                            setEditedSectionZone(element);
                            setEditedSectionIndex(index);
                          }}
                        >
                          <Edit size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </MediaQuery>
        </>
      ) : (
        <>No sections</>
      )}
      <Button
        onClick={() => {
          setEditedSection(newSection);
          setEditedSectionIndex(-1);
          setEditedSectionZone(element);
        }}
        fullWidth
        my="xl"
        color="cap-navy"
        variant="outline"
        leftIcon={<Plus />}
      >
        Section
      </Button>
    </td>
  );

  const rows = elements.map((element) => (
    <Fragment key={element.id}>
      <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.zoneName}</td>
          <SectionList element={element} />
          <td>{String(element.isActive ?? true)}</td>
          <td>
            <TableRowActions
              element={element}
              setEditedElement={setEditedZone}
              reloadCallback={reloadCallback}
              assetName="Zone"
              collectionName="areaForFleetUse"
              nameKey="zoneName"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
      <MediaQuery largerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>{element.zoneName}</td>
          <SectionList element={element} />
          <td>
            <TableRowActions
              element={element}
              setEditedElement={setEditedZone}
              reloadCallback={reloadCallback}
              assetName="Zone"
              collectionName="areaForFleetUse"
              nameKey="zoneName"
              idKey="id"
            />
          </td>
        </tr>
      </MediaQuery>
    </Fragment>
  ));

  return (
    <>
      {message && <Alert color={messageColor} onAnimationEnd={() => setTimeout(() => setMessage(undefined), 500)}>{message}</Alert>}
      {notification && notification.type !== "choice" && (
        <div style={{ position: "fixed", top: 76, right: 20, zIndex: 9999 }}>
          <Button color={notification.type === "success" ? "green" : "red"} variant="light" onClick={clearNotification}>
            {notification.message}
          </Button>
        </div>
      )}
      {elements.length > 0 && (
        <Table width="100%">
          <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
            <thead>
              <tr>
                <th>Zone name</th>
                <th>Sections</th>
                <th>Active</th>
                <th colSpan={7} style={{ textAlign: "left" }}>
                  <Menu
                    styles={{
                      item: {
                        minHeight: 44,
                        padding: "10px 16px",
                      },
                    }}
                    control={<Button variant="outline" color="cap-blue" size="sm" leftIcon={<FileExport size={16} />}>Actions</Button>}
                  >
                    <Menu.Item onClick={handleExportSectionsToFlexOptions} disabled={loading}>
                      {loading ? "Creating Flex Options..." : "Create Flex Options set"}
                    </Menu.Item>
                  </Menu>
                </th>
              </tr>
            </thead>
          </MediaQuery>
          <MediaQuery largerThan="sm" styles={{ display: "none" }}>
            <thead>
              <tr>
                <th>Zone name</th>
                <th>Sections</th>
                <th colSpan={7} style={{ textAlign: "left" }}>
                  <Menu
                    styles={{
                      item: {
                        minHeight: 44,
                        padding: "10px 16px",
                      },
                    }}
                    control={<Button variant="outline" color="cap-blue" size="sm" leftIcon={<FileExport size={16} />}>Actions</Button>}
                  >
                    <Menu.Item onClick={handleExportSectionsToFlexOptions} disabled={loading}>
                      {loading ? "Creating Flex Options..." : "Create Flex Options set"}
                    </Menu.Item>
                  </Menu>
                </th>
              </tr>
            </thead>
          </MediaQuery>
          <tbody>{rows}</tbody>
        </Table>
      )}
      {editedZone && (
        <Modal opened onClose={closeZoneEdit} title="Zone" {...({ fullScreen: isMobile } as any)}>
          <ZoneEditor
            asset={editedZone}
            assetConfig={ZoneAssetConfig}
            saveAssetCallback={onSaveZone}
            deleteAssetCallback={handleDeleteZone}
          ></ZoneEditor>
        </Modal>
      )}
      {editedSection && (
        <Modal opened onClose={closeSectionEdit} title="Section" {...({ fullScreen: isMobile } as any)}>
          <ZoneEditor
            asset={editedSection}
            assetConfig={SectionAssetConfig}
            saveAssetCallback={handleSaveSection}
            deleteAssetCallback={handleDeleteSection}
          ></ZoneEditor>
        </Modal>
      )}
      {notification && notification.type === "choice" && (
        <FlexOptionsChoiceModal
          opened={true}
          onClose={() => clearNotification()}
          onMerge={mergeOptions}
          onCreateNew={createNewSet}
          existingDocs={existingDocs}
          assetType="Sections"
        />
      )}
      <Button onClick={() => setEditedZone(newZone)} fullWidth my="xl" color="cap-navy" variant="outline" leftIcon={<Plus />}>
        Zone
      </Button>
    </>
  );
}
